import React, { useState } from 'react'
import ApiService from '../../../services/ApiService'
import OrdenoLogo from '../../../pages/Login/Login/img-login/ordeno-logo.png'

import {
  FullPageWrapper,
  Box,
  Logo,
  Title,
  Subtitle,
  FormRowFull,
  FormRowSplit,
  FieldGroup,
  FieldLabel,
  InputField,
  PasswordFieldWrapper,
  PasswordInput,
  PasswordToggle,
  PasswordChecks,
  PasswordCheckItem,
  SubmitButton,
  BackRow,
  BackIcon,
  BackText,
} from './style'

interface RegisterScreenProps {
  setPage: (page: string) => void
}

const RegisterScreen: React.FC<RegisterScreenProps> = ({ setPage }) => {
  const [accountInfo, setAccountInfo] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    cnpj: '',
    password: '',
    confirmPassword: '',
  })

  const [showPass, setShowPass] = useState(false)
  const [showConfirmPass, setShowConfirmPass] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setAccountInfo((prev) => ({ ...prev, [name]: value }))
  }

  // Regras da senha (iguais ao layout antigo / print)
  const hasMinLen = accountInfo.password.length >= 8
  const hasUpper = /[A-Z]/.test(accountInfo.password)
  const hasSpecialOrNumber = /[\d\W]/.test(accountInfo.password)

  const canSubmit =
    accountInfo.name &&
    accountInfo.email &&
    accountInfo.phone &&
    accountInfo.company &&
    accountInfo.cnpj &&
    accountInfo.password &&
    accountInfo.confirmPassword &&
    accountInfo.password === accountInfo.confirmPassword &&
    hasMinLen &&
    hasUpper &&
    hasSpecialOrNumber

  const handleRegister = async () => {
    if (!canSubmit || loading) return

    setLoading(true)
    setFormError(null)

    try {
      const cleanCnpj = accountInfo.cnpj.replace(/\D/g, '')
      const cleanPhone = accountInfo.phone.replace(/\D/g, '')

      await ApiService.auth.register({
        name: accountInfo.name,
        email: accountInfo.email,
        phone: cleanPhone,
        company: accountInfo.company,
        cnpj: cleanCnpj,
        password: accountInfo.password,
        adminUser: false,
        approved: 'pending',
        picture: '',
        permissions: [],
      })

      // mesmo comportamento do Register novo
      window.location.href = '/pendingmessage'
    } catch (err: any) {
      console.error('REGISTER ERROR', {
        url: err?.config?.url,
        status: err?.response?.status,
        data: err?.response?.data,
      })
      setFormError(
        err?.response?.data?.message || 'Não foi possível cadastrar.',
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <FullPageWrapper>
      <Box>
        {/* Logo */}
        <Logo src={OrdenoLogo} alt='ordeno' />

        {/* Título e subtítulo, igual ao print */}
        <Title>Cadastre-se</Title>
        <Subtitle>Preencha os campos abaixo para criar sua conta.</Subtitle>

        {/* Nome - linha inteira */}
        <FormRowFull>
          <FieldGroup>
            <FieldLabel htmlFor='name'>Nome</FieldLabel>
            <InputField
              id='name'
              name='name'
              type='text'
              placeholder='Digite aqui'
              value={accountInfo.name}
              onChange={handleChange}
            />
          </FieldGroup>
        </FormRowFull>

        {/* Email / Telefone */}
        <FormRowSplit>
          <FieldGroup>
            <FieldLabel htmlFor='email'>Email</FieldLabel>
            <InputField
              id='email'
              name='email'
              type='email'
              inputMode='email'
              autoComplete='email'
              placeholder='email@email.com'
              value={accountInfo.email}
              onChange={handleChange}
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor='phone'>Telefone</FieldLabel>
            <InputField
              id='phone'
              name='phone'
              type='text'
              placeholder='(00) 0000-0000'
              value={accountInfo.phone}
              onChange={handleChange}
            />
          </FieldGroup>
        </FormRowSplit>

        {/* Empresa / CNPJ */}
        <FormRowSplit>
          <FieldGroup>
            <FieldLabel htmlFor='company'>Empresa</FieldLabel>
            <InputField
              id='company'
              name='company'
              type='text'
              placeholder='Digite aqui'
              value={accountInfo.company}
              onChange={handleChange}
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor='cnpj'>CNPJ</FieldLabel>
            <InputField
              id='cnpj'
              name='cnpj'
              type='text'
              inputMode='numeric'
              autoComplete='off'
              placeholder='00.000.000/0000-00'
              value={accountInfo.cnpj}
              onChange={handleChange}
            />
          </FieldGroup>
        </FormRowSplit>

        {/* Senha / Confirmar senha */}
        <FormRowSplit>
          <FieldGroup>
            <FieldLabel htmlFor='password'>Senha</FieldLabel>

            <PasswordFieldWrapper>
              <PasswordInput
                id='password'
                name='password'
                type={showPass ? 'text' : 'password'}
                placeholder='Digite aqui'
                value={accountInfo.password}
                onChange={handleChange}
              />
              <PasswordToggle
                type='button'
                onClick={() => setShowPass(!showPass)}
                aria-label='Mostrar/ocultar senha'
              >
                👁
              </PasswordToggle>
            </PasswordFieldWrapper>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor='confirmPassword'>Confirmar senha</FieldLabel>

            <PasswordFieldWrapper>
              <PasswordInput
                id='confirmPassword'
                name='confirmPassword'
                type={showConfirmPass ? 'text' : 'password'}
                placeholder='Digite aqui'
                value={accountInfo.confirmPassword}
                onChange={handleChange}
              />
              <PasswordToggle
                type='button'
                onClick={() => setShowConfirmPass(!showConfirmPass)}
                aria-label='Mostrar/ocultar senha'
              >
                👁
              </PasswordToggle>
            </PasswordFieldWrapper>
          </FieldGroup>
        </FormRowSplit>

        {/* Checklist de senha, igual ao print */}
        <PasswordChecks>
          <PasswordCheckItem $ok={hasMinLen}>
            Deve ter no mínimo 8 caracteres
          </PasswordCheckItem>
          <PasswordCheckItem $ok={hasUpper}>
            Deve conter maiúsculas e minúsculas
          </PasswordCheckItem>
          <PasswordCheckItem $ok={hasSpecialOrNumber}>
            Deve ter ao menos um caractere especial
          </PasswordCheckItem>
        </PasswordChecks>

        {formError && (
          <div style={{ color: 'red', marginBottom: 8 }}>{formError}</div>
        )}

        {/* Botão azul */}
        <SubmitButton
          type='button'
          disabled={!canSubmit || loading}
          onClick={handleRegister}
        >
          {loading ? 'Enviando…' : 'Cadastrar'}
        </SubmitButton>

        {/* Voltar ao login */}
        <BackRow onClick={() => setPage('Main')}>
          <BackIcon>←</BackIcon>
          <BackText>Voltar ao login</BackText>
        </BackRow>
      </Box>
    </FullPageWrapper>
  )
}

export default RegisterScreen
